import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import EmployeeLoginForm from './components/EmployeeLoginForm';
import EmployeeHelpResources from './components/EmployeeHelpResources';
import TrustSignals from '../login/components/TrustSignals';
import CredentialsHelper from '../login/components/CredentialsHelper';

const EmployeeLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in as employee
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData?.role === 'employee') {
        navigate('/employee-portal');
      } else {
        // If logged in as different role, redirect to general login
        navigate('/login');
      }
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Side - Employee-Focused Branding */}
        <div className="lg:w-1/2 bg-emerald-600 text-white p-8 lg:p-12 flex flex-col justify-between">
          <div>
            {/* Logo */}
            <div className="flex items-center space-x-3 mb-12">
              <div className="flex items-center justify-center w-10 h-10 bg-white/20 rounded-lg">
                <Icon name="Users" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{t('employeeLoginPage.title')}</h1>
                <p className="text-emerald-100 text-sm">{t('brand.tagline')}</p>
              </div>
            </div>

            {/* Employee-focused messaging */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  Your Compensation Information
                </h2>
                <p className="text-emerald-100 text-lg leading-relaxed">
                  Secure access to your pay transparency data, compensation history, 
                  and peer comparisons in full compliance with EU privacy regulations.
                </p>
              </div>

              <div className="grid gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="Eye" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('employeeLoginPage.compensationTransparency')}</h3>
                    <p className="text-emerald-100 text-sm">
                      View your pay band, quartile position, and anonymized peer comparisons
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="Clock" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('employeeLoginPage.payHistory')}</h3>
                    <p className="text-emerald-100 text-sm">
                      Access your complete compensation history and progression tracking
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="MessageSquare" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('employeeLoginPage.informationRequests')}</h3>
                    <p className="text-emerald-100 text-sm">
                      Request additional compensation information through secure channels
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="Lock" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('employeeLoginPage.privacyProtected')}</h3>
                    <p className="text-emerald-100 text-sm">
                      Your personal data is encrypted and protected under GDPR compliance
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Employee-specific stats */}
          <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/20">
            <div className="text-center">
              <div className="text-2xl font-bold">100%</div>
              <div className="text-emerald-100 text-sm">{t('employeeLoginPage.dataPrivacy')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">24/7</div>
              <div className="text-emerald-100 text-sm">{t('employeeLoginPage.secureAccess')}</div>
            </div>
          </div>
        </div>

        {/* Right Side - Employee Login Form */}
        <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Header */}
            <div className="text-center">
              <h2 className="text-3xl font-bold text-foreground mb-2">{t('employeeLoginPage.access')}</h2>
              <p className="text-muted-foreground">
                Sign in to view your compensation information
              </p>
            </div>

            {/* Employee Login Form */}
            <EmployeeLoginForm />

            {/* Employee Help Resources */}
            <EmployeeHelpResources />

            {/* Demo Credentials Helper */}
            <CredentialsHelper />

            {/* Footer Links */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-6 text-sm">
                <button className="text-muted-foreground hover:text-foreground transition-smooth">
                  Employee Privacy
                </button>
                <button className="text-muted-foreground hover:text-foreground transition-smooth">
                  Data Rights
                </button>
                <button className="text-muted-foreground hover:text-foreground transition-smooth">
                  Help Center
                </button>
              </div>
              
              <div className="text-center">
                <button 
                  onClick={() => navigate('/login')}
                  className="text-sm text-primary hover:text-primary/80 transition-smooth"
                >
                  Admin/Manager Login →
                </button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                © {new Date()?.getFullYear()} PayTransparency Pro. Employee portal access is secure and GDPR compliant.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Trust Signals Footer */}
      <div className="bg-card border-t border-border">
        <div className="max-w-6xl mx-auto px-8 py-12">
          <TrustSignals />
        </div>
      </div>
    </div>
  );
};

export default EmployeeLogin;